import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /moj-translator — Ministry of Justice / Court-recognized sworn translator
import { createFileRoute } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE_MJ = "https://nyclanguageinstitute.com";

const TITLE = "นักแปลขึ้นทะเบียนกระทรวงยุติธรรม (MoJ Sworn Translator) | NYC";
export const DESC = "บริการแปลและรับรองเอกสารโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม (Court-recognized / MoJ Sworn Translator) สำหรับคดีศาล สัญญา คำพิพากษา — ปรึกษาฟรีก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ";

const steps = [
  { name: "ส่งเอกสารผ่าน LINE/Email", text: "ส่งสแกนชัดเจน 300dpi — ทีมประเมินราคาและเวลาส่งภายใน 30 นาทีในเวลาทำการ" },
  { name: "แปลโดย MoJ Sworn Translator", text: "นักแปลขึ้นทะเบียนศาลและกระทรวงยุติธรรม (ทะเบียนเลขที่ตรวจสอบได้) แปลด้วยมาตรฐานนิติศาสตร์" },
  { name: "ตรวจสอบโดยทนาย QA", text: "ทนายในทีมตรวจถ้อยคำกฎหมาย ความครบถ้วน และความถูกต้องทางกระบวนพิจารณา 2 รอบ" },
  { name: "ประทับตรา + ใบรับรองนักแปล", text: "ออกใบ Certificate of Translation พร้อมระบุชื่อ-เลขทะเบียนนักแปลเพื่อใช้ในศาล/สถานทูต" },
  { name: "ส่งฉบับจริงทั่วไทย/ทั่วโลก", text: "EMS ฟรีในไทย, DHL/FedEx ทั่วโลก (เก็บค่าขนส่งตามจริง)" },
];

const faqs = [
  { q: "MoJ Sworn Translator คือใคร?", a: "นักแปลที่ขึ้นทะเบียนกับกระทรวงยุติธรรม/ศาลยุติธรรมไทย มีอำนาจรับรองคำแปลในกระบวนพิจารณาคดี สามารถตรวจสอบทะเบียนได้กับ Office of the Judiciary" },
  { q: "ต่างจากนักแปล MFA อย่างไร?", a: "นักแปล MFA ขึ้นทะเบียนกับกระทรวงการต่างประเทศ ใช้สำหรับรับรองเอกสารยื่นสถานทูต — MoJ Translator ใช้สำหรับคดี/พยานหลักฐานในศาล — NYC มีทั้งสองประเภทพร้อมให้บริการ" },
  { q: "ใช้สำหรับคดีอะไรได้บ้าง?", a: "คดีแพ่ง อาญา ครอบครัว แรงงาน ทรัพย์สินทางปัญญา (พ.ร.บ.ลิขสิทธิ์ 2537), อนุญาโตตุลาการตาม พ.ร.บ.อนุญาโตตุลาการ 2545, คดีล้มละลาย, คดีปกครอง, และคดีที่ต้องส่งคำพิพากษาไทยไปบังคับใช้ในต่างประเทศตาม Hague Service Convention" },
  { q: "เอกสารใดต้องใช้ MoJ Translator?", a: "คำฟ้อง คำให้การ คำพิพากษา หมายเรียก หมายศาล หมายจับ สัญญาที่อ้างเป็นพยานหลักฐาน คำเบิกความพยานชาวต่างชาติ ใบสำคัญทางทะเบียน รายงานการสืบสวน DNA report และเอกสารต่างประเทศที่ยื่นต่อศาลตาม ป.วิ.แพ่ง มาตรา 46" },
  { q: "ค่าบริการคิดอย่างไร?", a: "ค่าบริการขึ้นกับคู่ภาษา จำนวนหน้า ความซับซ้อนของถ้อยคำทางกฎหมาย และความเร่งด่วน จึงประเมินเป็นรายงานให้หลังดูเอกสารจริง ส่งไฟล์สแกนมาทาง LINE โทรศัพท์ หรืออีเมล แล้วเจ้าหน้าที่จะแจ้งขั้นตอนและค่าบริการที่ตรงกับกรณีของคุณกลับไป" },
  { q: "ใช้เวลานานแค่ไหน?", a: "ระยะเวลาขึ้นกับจำนวนหน้าและคู่ภาษา โดยงานทุกชิ้นผ่านการตรวจทานสองรอบ (นักแปล + ทนายในทีม) กรณีมีวันนัดศาลกำหนดแน่นอน แจ้งวันที่ต้องใช้มาก่อน ทีมงานจะยืนยันกรอบเวลาที่ทำได้จริงก่อนเริ่มงาน" },

  { q: "ต้องไปยื่นศาลเองไหม?", a: "ไม่จำเป็น — ทีมทนายในเครือ NYC (ใบอนุญาตว่าความสภาทนายความ) รับยื่นคำร้อง ตอบหมาย เบิกพยานหลักฐาน และรับ-ส่งหมายแทนได้ทุกศาลในไทย ครอบคลุมศาลแพ่ง อาญา แรงงาน ทรัพย์สินทางปัญญา และศาลปกครอง" },
  { q: "รับรอง MFA เพิ่มได้ไหม?", a: "ได้ — ทำต่อเนื่องเป็นชุดเดียวได้: แปลและรับรองโดยนักแปลขึ้นทะเบียน → รับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ → ยื่นสถานทูตปลายทาง ทั้งนี้ประเทศไทยยื่นภาคยานุวัติสารอนุสัญญากรุงเฮก (Apostille) แล้ว และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ 28 กุมภาพันธ์ 2570 (ที่มา: HCCH) ก่อนถึงวันดังกล่าวยังต้องใช้ขั้นตอนกงสุล + สถานทูตตามเดิม" },

];

export const Route = createFileRoute("/moj-translator/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("moj", "th", "https://nyclanguageinstitute.com/moj-translator") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/moj-translator", "th")}, { name: "description", content: ctaDesc(DESC, "/moj-translator", "th")},
      { name: "keywords", content: "นักแปลกระทรวงยุติธรรม, MoJ Sworn Translator, นักแปลศาล, court translator Thailand, แปลคำพิพากษา, แปลคดี" },
      { property: "og:title", content: ctaTitle(TITLE, "/moj-translator", "th")}, { property: "og:description", content: ctaDesc(DESC, "/moj-translator", "th")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/moj-translator" },
    ],
    links: canonicalAndHreflang("/moj-translator"),
    scripts: [
      ...buildLandingSchemas({
        path: "/moj-translator", title: TITLE, description: DESC, serviceType: "Sworn Translation Service", priceFromThb: 1500,
        steps, faqs,
        breadcrumbs: [{ name: "Home", path: "/" }, { name: "บริการ", path: "/services" }, { name: "MoJ Translator", path: "/moj-translator" }],
      }),
      entitySameAsSchema(),
      softwareApplicationSchema({ url: `${SITE_MJ}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing MoJ / court-recognized sworn translation tooling." }),
      claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(`${SITE_MJ}/moj-translator`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: () => (
    <ServiceLanding
      badge="MoJ / Court-recognized"
      title="นักแปลขึ้นทะเบียนกระทรวงยุติธรรม (Sworn Translator)"
      subtitle={DESC}
      quickAnswer={{
        q: "MoJ Sworn Translator คือใคร และใช้กับเอกสารอะไร?",
        a: "นักแปลขึ้นทะเบียนกับศาลและกระทรวงยุติธรรมไทย (ตรวจสอบทะเบียนได้กับ Office of the Judiciary) ทำคำแปลที่ใช้เป็นพยานหลักฐานในกระบวนพิจารณาคดีได้ — คำฟ้อง, คำให้การ, คำพิพากษา, สัญญา, หมายศาล สอบถามขั้นตอน กรอบเวลา และค่าบริการที่ตรงกับเอกสารของคุณได้ทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd",
      }}
      heroBullets={[
        "นักแปลทะเบียนศาลและกระทรวงยุติธรรม (ตรวจสอบได้)",
        "ทนายในทีมตรวจคุณภาพ 2 รอบ",
        "ใช้ได้ทุกชั้นศาล + อนุญาโตตุลาการ",
        "ต่อยอด MFA + Consular ในแพ็กเกจเดียว",
      ]}
      whoFor={{
        th: ["ทนายความที่ต้องส่งพยานหลักฐานภาษาต่างประเทศ", "บริษัทที่ฟ้องคดีระหว่างประเทศ", "ผู้ที่ต้องบังคับใช้คำพิพากษาต่างประเทศในไทย"],
        foreign: ["Foreign litigants in Thai courts needing certified translations", "International law firms handling Thai-related disputes"],
      }}
      steps={steps}
      pricing={[
        { label: "แปลกฎหมาย อังกฤษ-ไทย / ไทย-อังกฤษ", price: "สอบถามเจ้าหน้าที่", note: "ประเมินตามจำนวนหน้าและความเร่งด่วน" },
        { label: "ภาษาอื่น (จีน/ญี่ปุ่น/เกาหลี/เยอรมัน ฯลฯ)", price: "สอบถามเจ้าหน้าที่", note: "ขึ้นกับคู่ภาษาและคิวนักแปล" },
        { label: "Certificate of Translation", price: "รวมในงานแปล", note: "พร้อมข้อมูลผู้แปลผู้รับรอง" },
      ]}
      faqs={faqs}
      relatedLinks={[
        { to: "/moj-translator/documents", label: "เอกสารคดีที่รับแปลและรับรอง (6 กลุ่ม)" },
        { to: "/moj-translator/faq", label: "คำถามที่พบบ่อย MoJ" },
        { to: "/translate/mfa", label: "แปล + รับรอง MFA" },
        { to: "/notary", label: "Notary Public" },
        { to: "/translate/naati", label: "NAATI Australia" },
        { to: "/embassy", label: "Embassy Legalization" },
        { to: "/services", label: "บริการทั้งหมด" },
        { to: "/blog/moj-court-sworn-translator-thailand", label: "บทความ: MoJ Translator" },
      ]}
    />
  ),
});
